import FormBox from "./FormBox";
import { MAX_DOCS_PER_SESSION } from "./researchUploadConstants";
import {
  type ResearchAttachmentRecord,
  formatAttachmentAnswer,
  uploadResearchAttachment
} from "@lib/agent/researchIntake";
import Icon from "@lib/common/components/Icon/Icon";
import { Button, FileButton, Group, Pill, Stack, Text } from "@mantine/core";
import { useState } from "react";

interface Props {
  placeholder?: string | null;
  onSend: (v: string) => void;
}

export default function ResearchUploadAffordance({ placeholder, onSend }: Props) {
  const [records, setRecords] = useState<ResearchAttachmentRecord[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  async function handlePick(files: File[]) {
    setError(null);
    setBusy(true);
    try {
      const room = MAX_DOCS_PER_SESSION - records.length;
      const uploaded = await Promise.all(files.slice(0, room).map(f => uploadResearchAttachment(f)));
      setRecords(prev => [...prev, ...uploaded]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  function handleSubmit() {
    if (submitted) {
      return;
    }
    setSubmitted(true);
    onSend(formatAttachmentAnswer(records));
  }

  if (submitted) {
    return null;
  }

  return (
    <FormBox>
      <Stack gap="lg">
        {records.length > 0 && (
          <Group gap="xs" wrap="wrap">
            {records.map(r => (
              <Pill
                key={r.attachment_id}
                withRemoveButton
                onRemove={() => setRecords(prev => prev.filter(x => x.attachment_id !== r.attachment_id))}
              >
                {r.filename}
              </Pill>
            ))}
          </Group>
        )}
        <Group gap="xs">
          <FileButton<true>
            onChange={handlePick}
            accept=".pdf,.doc,.docx,.txt,.md"
            multiple
            disabled={busy || records.length >= MAX_DOCS_PER_SESSION}
          >
            {props => (
              <Button
                size="sm"
                variant="light"
                loading={busy}
                disabled={records.length >= MAX_DOCS_PER_SESSION}
                leftSection={<Icon name="paperclip" size="0.7rem" variant="solid" />}
                {...props}
              >
                {placeholder ?? "Attach documents"}
              </Button>
            )}
          </FileButton>
          <Button
            size="sm"
            variant="light"
            onClick={handleSubmit}
            leftSection={<Icon name="check" size="0.7rem" variant="solid" />}
          >
            {records.length > 0 ? "Use these documents" : "Skip"}
          </Button>
        </Group>
        {error !== null && (
          <Text size="xs" c="red" role="alert">
            {error}
          </Text>
        )}
      </Stack>
    </FormBox>
  );
}
